import { DeleteOutlined, EditOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Order, OrderWorkLog } from '@services';
import { formatLoggedTime, workLogMinutes } from '@utils';
import { Button, Popconfirm, Space, Tag, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';

const { Text } = Typography;

/** Order.userId is typed as a string on the portal side while the log carries the numeric id. */
export const isAssignee = (userId: number, order: { userId?: string | number | null }) =>
  order.userId != null && String(userId) === String(order.userId);

interface IProps {
  order: Order;
  /** Phone: the card itself opens the editor, so the row offers Remove only. */
  isMobile: boolean;
  canEdit: (log: OrderWorkLog) => boolean;
  openEdit: (log: OrderWorkLog) => void;
  handleDelete: (log: OrderWorkLog) => Promise<void>;
}

export const Columns = ({ order, isMobile, canEdit, openEdit, handleDelete }: IProps) => [
  {
    title: 'Technician',
    dataIndex: ['user', 'fullname'],
    key: 'user',
    render: (_: unknown, log: OrderWorkLog) => (
      <Space size={4}>
        <span>{log.user?.fullname ?? `#${log.userId}`}</span>
        {isAssignee(log.userId, order) && <Tag color="blue">Assigned</Tag>}
      </Space>
    ),
  },
  {
    title: 'Service',
    dataIndex: 'serviceTitle',
    key: 'serviceTitle',
    render: (title: string | null | undefined, log: OrderWorkLog) => {
      const stillOnOrder =
        log.serviceId != null &&
        order.serviceOrders.some((line) => line.serviceId === log.serviceId);
      const label =
        log.serviceId == null ? (
          <Text italic>Whole job</Text>
        ) : stillOnOrder ? (
          title
        ) : (
          <Text type="secondary">{title} (removed)</Text>
        );
      // The note is a hint, not a column: most entries have none and a long one would squash the table.
      return (
        <Space size={6}>
          {label}
          {log.note && (
            <Tooltip title={log.note}>
              <InfoCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
            </Tooltip>
          )}
        </Space>
      );
    },
  },
  {
    title: 'When',
    dataIndex: 'workedFrom',
    key: 'workedFrom',
    render: (_: unknown, log: OrderWorkLog) => (
      <Space size={4}>
        <span>
          {dayjs(log.workedFrom).format('DD/MM/YYYY')}
          <Text type="secondary"> · </Text>
          {dayjs(log.workedFrom).format('HH:mm')}–
          {log.workedTo ? dayjs(log.workedTo).format('HH:mm') : 'now'}
        </span>
        {!log.workedTo && <Tag color="processing">Running</Tag>}
      </Space>
    ),
  },
  {
    title: 'Time',
    key: 'minutes',
    align: 'right' as const,
    render: (_: unknown, log: OrderWorkLog) => (
      <Text strong>{formatLoggedTime(workLogMinutes(log))}</Text>
    ),
  },
  {
    title: '',
    key: 'actions',
    width: 180,
    render: (_: unknown, log: OrderWorkLog) => {
      if (!canEdit(log)) return null;

      const remove = (
        <Popconfirm
          title="Remove this time entry?"
          okText="Remove"
          okButtonProps={{ danger: true }}
          onConfirm={() => handleDelete(log)}
        >
          {isMobile ? (
            <Button danger size="small" icon={<DeleteOutlined />}>
              Remove
            </Button>
          ) : (
            <Button size="small" danger icon={<DeleteOutlined />}>
              Remove
            </Button>
          )}
        </Popconfirm>
      );

      // On a phone the card itself opens the editor, so there is no edit icon and the remove
      // button must not let its tap fall through to the card.
      if (isMobile) {
        return <span onClick={(e) => e.stopPropagation()}>{remove}</span>;
      }

      return (
        <Space size={8}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(log)}>
            Edit
          </Button>
          {remove}
        </Space>
      );
    },
  },
];
